import { useRef, useState, useCallback } from 'react';

interface RateLimitReturn {
  checkRateLimit: () => boolean;
  isBlocked: boolean;
  remainingTime: number;
  attemptsRemaining: number;
}

export function useRateLimit(maxAttempts: number, windowMs: number): RateLimitReturn {
  const attemptsRef = useRef<number[]>([]);
  const [isBlocked, setIsBlocked] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const [attemptsRemaining, setAttemptsRemaining] = useState(maxAttempts);

  const checkRateLimit = useCallback((): boolean => {
    const now = Date.now();

    // Remove attempts outside the time window
    attemptsRef.current = attemptsRef.current.filter(
      timestamp => now - timestamp < windowMs
    );

    if (attemptsRef.current.length >= maxAttempts) {
      const oldestAttempt = attemptsRef.current[0];
      const timeUntilReset = windowMs - (now - oldestAttempt);

      setIsBlocked(true);
      setRemainingTime(Math.ceil(timeUntilReset / 1000));
      setAttemptsRemaining(0);

      // Auto-unblock when time expires
      setTimeout(() => {
        setIsBlocked(false);
        setRemainingTime(0);
        attemptsRef.current = attemptsRef.current.filter(
          timestamp => Date.now() - timestamp < windowMs
        );
        setAttemptsRemaining(maxAttempts - attemptsRef.current.length);
      }, timeUntilReset);

      return false;
    }

    attemptsRef.current.push(now);
    setAttemptsRemaining(Math.max(0, maxAttempts - attemptsRef.current.length));
    return true;
  }, [maxAttempts, windowMs]);

  return { checkRateLimit, isBlocked, remainingTime, attemptsRemaining };
}
